import MaterialTable from "@material-table/core";
import {
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close, Done, GetApp, Refresh } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import DateRangeField from "../components/DateRangeField/DateRangeField";
import LinkButton from "../components/LinkButton/LinkButton";
import Loading from "../components/Loading";
import { usePopups } from "../contexts/PopupContext";
import { useGlobalSnackbar } from "../contexts/SnackbarContext";
import { useUser } from "../contexts/UserContextProvider";
import useApi from "../hooks/useApi";
import {
  dateFormatter,
  dateFormatterComplete,
  downloadFile,
  getCurrentWeek,
  getSessionEnd,
  getSessionStart,
  onlyDateFormatter,
} from "../utils/utils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  content: {
    fontSize: "3ex",
  },
  card: {
    padding: "2%",
  },
  title: {
    fontWeight: "lighter",
    paddingBottom: "1%",
    fontSize: "1.5rem",
  },
  paper: {
    padding: "1rem",
    marginBlockEnd: "1rem",
    display: "flex",
    justifyContent: "end",
    gap: "1rem",
    flexWrap: "wrap",
  },
  paperData: {
    gap: ".5rem",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  wrapperTabs: {
    padding: "0 1rem",
  },
});

export default function PatientProfile() {
  const classes = useStyles();
  const { t } = useTranslation(["views", "common"]);
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);

  const { id: patientId } = useParams();
  const [patient, setPatient] = useState([]);
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [range, setRange] = useState(getCurrentWeek);

  const count = useMemo(() => {
    return items.filter((session) => {
      const sessionStart = getSessionStart(session);
      return (
        sessionStart && range?.[0] < sessionStart && range?.[1] > sessionStart
      );
    }).length;
  }, [items, range?.[0], range?.[1]]);

  const [user] = useUser();
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const theme = useTheme();
  const api = useApi();
  const [, open] = usePopups();
  const [downloadingRow, setDownloadingRow] = useState(null);

  const setGlobalSnackbar = useGlobalSnackbar();

  const loadPatient = async () => {
    setLoading(true);
    const response = await api(`patients/${patientId}`, "GET", null, () => {
      open("sessionExpiredPopup");
    });
    setLoading(false);
    if (response.ok === false) {
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.patient")}`,
      });
      return;
    }
    setPatient(response);
  };

  const loadPatientSessions = async () => {
    if (!patient?.idPatient || !patient?.idTherapist) return;
    setLoadingSessions(true);
    const response = await api(
      `sessions/patient/${patient.idPatient}/${patient.idTherapist}`,
      "GET",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    setLoadingSessions(false);
    if (response.ok === false) {
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.sessions_alt")}`,
      });
      return;
    }
    setItems(response);
  };

  const loadSignedDocument = async () => {
    if (!patient?.idPatient) return;
    setLoadingDocuments(true);
    const response = await api(
      `ccdocuments/${patient.idTherapist}/${patient.idPatient}`,
      "GET",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    setLoadingDocuments(false);
    if (response.ok === false) {
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.sessions_alt")}`,
      });
      return;
    }
    setDocuments(response);
  };

  const documentDownload = async (rowData) => {
    setDownloadingRow(rowData.id);
    setGlobalSnackbar({
      open: true,
      severity: "info",
      message: `${t("common:info.download_file")} ${rowData?.fileName} ${t("common:info.start_shortly")}`,
    });
    const response = await api(
      `ccdocuments/download/${rowData?.id}`,
      "GET",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    if (response.ok === false) {
      setDownloadingRow(null);
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.download_document")}`,
      });
    }
    try {
      downloadFile(response);
      setDownloadingRow(null);
      // setGlobalSnackbar({
      //   open: true,
      //   severity: "success",
      //   message: `${t("common:success.document")} ${rowData?.fileName} ${t("common:success.download")}`,
      // });
    } catch (e) {
      setDownloadingRow(null);
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.download_document")}`,
      });
    }
  };

  useEffect(() => {
    loadPatient();
  }, [patientId]);

  useEffect(() => {
    loadPatientSessions();
    loadSignedDocument();
  }, [patient.id]);
  return (
    <>
      <Loading loading={loading} />
      <div className={classes.card}>
        <Typography className={classes.title} variant="h4">
          {t("views:patient_profile.title")} {patient?.name ?? ""}{" "}
          {patient?.lastName ?? ""}
        </Typography>
        <Paper className={classes.paper}>
          <div style={{ marginRight: "auto" }} className={classes.paperData}>
            {t("views:patient_profile.session_done")}{" "}
            <DateRangeField
              range={range}
              onChange={(newRange) => setRange(newRange)}
            />
            : {loadingSessions ? <Skeleton width={30} /> : count}
          </div>
          <div className={classes.paperData}>
            {t("views:patient_profile.patient_code")}{" "}
            {patient?.patientCode || "-"}
          </div>
          <div className={classes.paperData}>
            {t("views:patient_profile.therapist")}{" "}
            {patient?.therapistFirstName ?? ""}{" "}
            {patient?.therapistLastName ?? ""}
          </div>
        </Paper>
        <div style={{ backgroundColor: "#fff" }}>
          <Tabs
            className={classes.wrapperTabs}
            value={activeTab}
            textColor="primary"
            indicatorColor="primary"
            onChange={(_, newValue) => setActiveTab(newValue)}
          >
            <Tab label={t("views:patient_profile.session_label")} />
            <Tab label={t("views:patient_profile.signed_document")} />
          </Tabs>
          {activeTab == 0 && (
            <div className={classes.content}>
              <MaterialTable
                isLoading={loadingSessions}
                theme={theme}
                columns={[
                  {
                    title: `${t("common:label.therapist")}`,
                    sorting: false,
                    render: (rowData) => (
                      <Tooltip title={t("common:tooltip.therapist_profile")}>
                        <span>
                          <LinkButton
                            onClick={() => {
                              history.push(
                                `/therapists/${rowData.idTherapist}`,
                              );
                            }}
                          >{`${rowData.therapistLastname ?? ""} ${
                            rowData.therapistFirstName ?? ""
                          }`}</LinkButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: `${t("common:label.start_session")}`,
                    field: "sessionStart",
                    type: "datetime",
                    searchable: false,
                    defaultSort: "desc",
                    render: (row) => {
                      const sessionStart = getSessionStart(row);
                      const sessionEnd = getSessionEnd(row);
                      return sessionStart
                        ? dateFormatterComplete.format(sessionStart)
                        : sessionEnd
                          ? `${t("common:label.cancelled_session")}`
                          : `${t("common:label.not_started_session")}`;
                    },
                    customFilterAndSearch: (term, row) => {
                      const sessionStart = getSessionStart(row);
                      const sessionEnd = getSessionEnd(row);
                      return (
                        sessionStart
                          ? onlyDateFormatter.format(sessionStart)
                          : sessionEnd
                            ? `${t("common:label.cancelled_session")}`
                            : `${t("common:label.not_yet_started_session")}`
                      )
                        .toLowerCase()
                        .includes(term.toLowerCase());
                    },
                  },
                  {
                    title: `${t("common:label.end_session")}`,
                    field: "sessionEnd",
                    searchable: false,
                    type: "datetime",
                    render: (row) => {
                      const sessionStart = getSessionStart(row);
                      const sessionEnd = getSessionEnd(row);
                      return sessionStart
                        ? sessionEnd
                          ? dateFormatterComplete.format(sessionEnd)
                          : `${t("common:label.session_progress")}`
                        : sessionEnd
                          ? `${t("common:label.cancelled_session")}`
                          : `${t("common:label.not_started_session")}`;
                    },
                    customFilterAndSearch: (term, row) => {
                      const sessionStart = getSessionStart(row);
                      const sessionEnd = getSessionEnd(row);
                      return (
                        sessionStart
                          ? onlyDateFormatter.format(sessionStart)
                          : sessionEnd
                            ? `${t("common:label.cancelled_session")}`
                            : `${t("common:label.not_yet_started_session")}`
                      )
                        .toLowerCase()
                        .includes(term.toLowerCase());
                    },
                  },
                  {
                    title: "PsyCare",
                    filtering: false,
                    render: (row) => {
                      return row.isTimer || row.isExternal ? (
                        <Close style={{ color: "red" }} />
                      ) : (
                        <Done style={{ color: theme.palette.primary.main }} />
                      );
                    },
                    customSort: (a, b) => {
                      const valA = a.isTimer || a.isExternal ? 1 : 0;
                      const valB = b.isTimer || b.isExternal ? 1 : 0;
                      return valA - valB;
                    },
                    searchable: false,
                  },
                ]}
                actions={[
                  {
                    icon: Refresh,
                    isFreeAction: true,
                    onClick: loadPatientSessions,
                  },
                ]}
                data={items}
                options={{
                  showTitle: false,
                  draggable: false,
                  thirdSortClick: false,
                  toolbar: true,
                  rowStyle: (rowData) => ({
                    fontSize: "1rem",
                  }),
                }}
                localization={{
                  header: {
                    actions: "",
                  },
                  body: {
                    emptyDataSourceMessage: (
                      <>
                        {t("views:patient_profile.empty_table_title")} <br />
                        {t("views:patient_profile.empty_table_subtitle")}
                      </>
                    ),
                  },
                  toolbar: {
                    searchPlaceholder: `${t("common:table.search_placeholder")}`,
                    searchTooltip: `${t("common:table.search_tooltip")}`,
                  },
                  pagination: {
                    labelDisplayedRows: `{from}-{to} ${t("common:table.displayed_rows")} {count}`,
                    labelRowsSelect: `${t("common:table.rows")}`,
                    labelRowsPerPage: `${t("common:table.rows_page")}`,
                    firstTooltip: `${t("common:table.first_tooltip")}`,
                    lastTooltip: `${t("common:table.last_tooltip")}`,
                    nextTooltip: `${t("common:table.next_tooltip")}`,
                    previousTooltip: `${t("common:table.previous_tooltip")}`,
                  },
                }}
              />
            </div>
          )}
          {activeTab == 1 && (
            <div className={classes.content}>
              <MaterialTable
                isLoading={loadingDocuments}
                theme={theme}
                columns={[
                  {
                    title: `${t("common:label.document_title")}`,
                    field: "fileName",
                    customSort: (a, b) =>
                      a.fileName.localeCompare(b.fileName, undefined, {
                        sensitivity: "base",
                      }),
                  },
                  {
                    title: `${t("common:label.therapist")}`,
                    type: "string",
                    sorting: false,
                    render: (rowData) => (
                      <Tooltip title={t("common:tooltip.therapist_profile")}>
                        <span>
                          <LinkButton
                            onClick={() => {
                              history.push(
                                `/therapists/${rowData.idTherapistOwner}`,
                              );
                            }}
                          >{`${rowData.therapistName ?? ""} ${
                            rowData.therapistSurname ?? ""
                          }`}</LinkButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: `${t("common:label.date_upload")}`,
                    customSort: (rowDataA, rowDataB) => {
                      const dateA = rowDataA.dateUpload
                        ? new Date(`${rowDataA.dateUpload}Z`)
                        : null;
                      const dateB = rowDataB.dateUpload
                        ? new Date(`${rowDataB.dateUpload}Z`)
                        : null;
                      if (dateA && dateB && !isNaN(dateA) && !isNaN(dateB)) {
                        return dateA - dateB;
                      }
                      if (!dateA || isNaN(dateA)) {
                        return -1;
                      }
                      if (!dateB || isNaN(dateB)) {
                        return 1;
                      }
                      return 0;
                    },
                    customFilterAndSearch: (term, rowData) =>
                      `${rowData.dateUpload}`
                        .toLowerCase()
                        .includes(term.toLowerCase()),
                    defaultSort: "desc",
                    searchable: false,
                    type: "datetime",
                    render: (rowData) =>
                      rowData?.dateUpload
                        ? dateFormatter.format(
                            new Date(`${rowData.dateUpload}Z`),
                          )
                        : "-",
                  },
                  {
                    title: `${t("common:label.signed")}`,
                    type: "datetime",
                    searchable: false,
                    customSort: (a, b) => {
                      const dateA = a.firmedDate
                        ? new Date(a.firmedDate).getTime()
                        : 0;
                      const dateB = b.firmedDate
                        ? new Date(b.firmedDate).getTime()
                        : 0;

                      return dateA - dateB;
                    },
                    customFilterAndSearch: (term, rowData) => {
                      const text = rowData.firmedDate
                        ? dateFormatter.format(new Date(rowData.firmedDate))
                        : `${t("common:label.waiting_signed")}`;

                      return text.toLowerCase().includes(term.toLowerCase());
                    },
                    render: (rowData) => (
                      <>
                        {rowData.firmedDate !== null ? (
                          <span style={{ color: "green" }}>
                            {" "}
                            {dateFormatter.format(
                              new Date(`${rowData.firmedDate}`),
                            )}{" "}
                          </span>
                        ) : (
                          <span style={{ color: "red" }}>
                            {t("common:label.waiting_signed")}
                          </span>
                        )}
                      </>
                    ),
                  },
                  {
                    title: "",
                    render: (rowData) => (
                      <>
                        {rowData.firmedDate !== null ? (
                          <Tooltip title={t("common:label.download")}>
                            <IconButton
                              disabled={downloadingRow === rowData.id}
                              onClick={() => documentDownload(rowData)}
                            >
                              <GetApp style={{ cursor: "pointer" }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </>
                    ),
                  },
                ]}
                actions={[
                  {
                    icon: Refresh,
                    isFreeAction: true,
                    onClick: loadSignedDocument,
                  },
                ]}
                data={documents}
                options={{
                  thirdSortClick: false,
                  draggable: false,
                  showTitle: false,
                  toolbar: true,
                  rowStyle: (rowData) => ({
                    fontSize: "1rem",
                  }),
                }}
                localization={{
                  header: {
                    actions: "",
                  },
                  body: {
                    emptyDataSourceMessage: (
                      <>
                        {t("views:patient_profile.empty_documents_title")}{" "}
                        <br />
                        {t("views:patient_profile.empty_documents_subtitle")}
                      </>
                    ),
                  },
                  toolbar: {
                    searchPlaceholder: `${t("common:table.search_placeholder")}`,
                    searchTooltip: `${t("common:table.search_tooltip")}`,
                  },
                  pagination: {
                    labelDisplayedRows: `{from}-{to} ${t("common:table.displayed_rows")} {count}`,
                    labelRowsSelect: `${t("common:table.rows")}`,
                    labelRowsPerPage: `${t("common:table.rows_page")}`,
                    firstTooltip: `${t("common:table.first_tooltip")}`,
                    lastTooltip: `${t("common:table.last_tooltip")}`,
                    nextTooltip: `${t("common:table.next_tooltip")}`,
                    previousTooltip: `${t("common:table.previous_tooltip")}`,
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
